import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, ArrowRight, ShieldCheck, HeartHandshake, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import Logo from '@/components/Logo';
import Footer from '@/components/Footer';
import { LOGIN, LOGIN_EXT } from '@/constants/testIds';

export default function Login() {
	const { loginWithEmail, formatApiError } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const from = location.state?.from?.pathname || '/dashboard';

	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [showPw, setShowPw] = useState(false);
	const [remember, setRemember] = useState(true);
	const [submitting, setSubmitting] = useState(false);

	const handleEmailLogin = async (e) => {
		e.preventDefault();
		setSubmitting(true);
		try {
			await loginWithEmail(email.trim(), password);
			toast.success('Welcome back');
			navigate(from, { replace: true });
		} catch (err) {
			toast.error(formatApiError(err, 'Login failed'));
		} finally {
			setSubmitting(false);
		}
	};

	const handleGoogle = () => {
		// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
		const redirectUrl = window.location.origin + '/dashboard';
		window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
	};

	return (
		<div className="min-h-screen grid lg:grid-cols-2">
			{/* Left: Brand panel — solid emerald→deep-blue gradient with the new logo. */}
			<div className="hidden lg:flex flex-col justify-between p-10 xl:p-14 relative overflow-hidden text-white"
				style={{ background: 'linear-gradient(135deg, #0B1F4D 0%, #15327A 38%, #0F5E63 72%, #10B981 100%)' }}
			>
				{/* soft orbs for depth */}
				<div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 rounded-full bg-emerald-400/20 blur-3xl" />
				<div className="pointer-events-none absolute bottom-0 right-0 w-96 h-96 rounded-full bg-blue-300/10 blur-3xl" />

				<div className="relative z-10">
					<Logo size="lg" textClassName="text-white" />
				</div>

				<div className="relative z-10 max-w-lg animate-fade-in-up">
					<h1 className="font-heading text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight">
						If something unexpected happens tomorrow,
						<br />
						<span className="text-emerald-300">how prepared is your family?</span>
					</h1>
					<p className="mt-6 text-white/90 text-lg leading-relaxed">
						Measure your Family Resilience Score. Improve it every month. Protect what matters most.
					</p>

					<div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
						<div className="rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 p-4">
							<ShieldCheck className="w-5 h-5 text-emerald-300" />
							<div className="mt-2 text-sm font-semibold">Resilience first</div>
							<div className="text-xs text-white/70 mt-1">Not just finances.</div>
						</div>
						<div className="rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 p-4">
							<HeartHandshake className="w-5 h-5 text-emerald-300" />
							<div className="mt-2 text-sm font-semibold">Family coach</div>
							<div className="text-xs text-white/70 mt-1">Educate. Coach. Motivate.</div>
						</div>
						<div className="rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 p-4">
							<Sparkles className="w-5 h-5 text-emerald-300" />
							<div className="mt-2 text-sm font-semibold">Tiny missions</div>
							<div className="text-xs text-white/70 mt-1">Repeated with love.</div>
						</div>
					</div>
				</div>

				<div className="relative z-10 text-sm text-white/70">
					Your family&apos;s information belongs to you. MyFamShield is designed with privacy first.
				</div>
			</div>

			{/* Right: Auth */}
			<div className="flex items-center justify-center p-6 sm:p-10 bg-[#F8FAFC]">
				<div className="w-full max-w-md">
					<div className="lg:hidden mb-8"><Logo size="md" /></div>
					<div className="mb-8">
						<h2 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight text-slate-900">
							Welcome back.
						</h2>
						<p className="mt-2 text-slate-600">
							Prepare your family. Not just your finances.
						</p>
					</div>

					<Button
						type="button"
						onClick={handleGoogle}
						data-testid={LOGIN_EXT.googleButton}
						className="w-full h-12 rounded-full bg-white text-slate-800 border border-slate-200 hover:bg-slate-50 shadow-sm font-medium gap-3"
					>
						<svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
							<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.74h3.57c2.08-1.92 3.28-4.74 3.28-8.06z" fill="#4285F4"/>
							<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.74c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
							<path d="M5.84 14.13a6.83 6.83 0 0 1 0-4.27V7.02H2.18a11 11 0 0 0 0 9.96l3.66-2.85z" fill="#FBBC05"/>
							<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.02l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
						</svg>
						Continue with Google
					</Button>

					<div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-slate-400">
						<div className="flex-1 h-px bg-slate-200" />
						<span>or sign in with email</span>
						<div className="flex-1 h-px bg-slate-200" />
					</div>

					<form onSubmit={handleEmailLogin} className="space-y-4">
						<div>
							<Label htmlFor="email" className="text-slate-700">Email</Label>
							<div className="relative mt-1.5">
								<Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
								<Input
									id="email"
									type="email"
									autoComplete="email"
									required
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									data-testid={LOGIN.emailInput}
									className="pl-10 h-12 rounded-xl"
									placeholder="you@family.com"
								/>
							</div>
						</div>
						<div>
							<div className="flex items-center justify-between">
								<Label htmlFor="password" className="text-slate-700">Password</Label>
								<Link
									to="/forgot-password"
									data-testid={LOGIN.forgotPasswordLink}
									className="text-xs font-semibold text-emerald-700 hover:underline"
								>
									Forgot password?
								</Link>
							</div>
							<div className="relative mt-1.5">
								<Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
								<Input
									id="password"
									type={showPw ? 'text' : 'password'}
									autoComplete="current-password"
									required
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									data-testid={LOGIN.passwordInput}
									className="pl-10 pr-10 h-12 rounded-xl"
									placeholder="••••••••"
								/>
								<button
									type="button"
									onClick={() => setShowPw((v) => !v)}
									className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
									aria-label="Toggle password visibility"
								>
									{showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
								</button>
							</div>
						</div>

						<div className="flex items-center gap-2">
							<Checkbox
								id="remember"
								checked={remember}
								onCheckedChange={(v) => setRemember(Boolean(v))}
								data-testid={LOGIN_EXT.rememberMe}
							/>
							<Label htmlFor="remember" className="text-sm text-slate-600">Remember me on this device</Label>
						</div>

						<Button
							type="submit"
							disabled={submitting}
							data-testid={LOGIN.submitButton}
							className="w-full h-12 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-semibold shadow hover:-translate-y-0.5 transition-all"
						>
							{submitting ? 'Signing in…' : (
								<>Sign in <ArrowRight className="w-4 h-4 ml-1" /></>
							)}
						</Button>
					</form>

					<p className="mt-8 text-sm text-slate-600 text-center">
						New here?{' '}
						<Link to="/register" data-testid="login-create-account-link" className="font-semibold text-emerald-700 hover:underline">
							Create your family account
						</Link>
					</p>
					<Footer compact className="mt-10" />
				</div>
			</div>
		</div>
	);
}
